/**
 * Clown.art — Lightbox image viewer
 * Web component vanilla, sans deps. Une seule instance par page.
 *
 * Usage : tout élément avec [data-clown-lightbox] devient cliquable.
 *   Attributs lus :
 *     data-clown-lightbox       — active le trigger
 *     data-lightbox-group       — id de groupe pour navigation (gallery / hero / feed / etc)
 *     data-full-src             — URL image full size (fallback : <img src>)
 *     data-full-srcset          — srcset image full size (optionnel)
 *     data-full-alt             — alt (fallback : <img alt>)
 *
 * Navigation :
 *   - clic prev/next, flèches ← →
 *   - fermer : ×, Escape, clic sur backdrop
 *   - boucle infinie (de la dernière vers la première et inverse)
 *   - swipe horizontal sur mobile (>40px de delta)
 */
(function () {
  if (window.__clownLightboxInit) return;
  window.__clownLightboxInit = true;

  class ClownLightbox extends HTMLElement {
    connectedCallback() {
      this.imageEl = this.querySelector('[data-lightbox-image]');
      this.captionEl = this.querySelector('[data-lightbox-caption]');
      this.counterEl = this.querySelector('[data-lightbox-counter]');
      this.spinnerEl = this.querySelector('[data-lightbox-spinner]');
      this.closeBtn = this.querySelector('[data-lightbox-close]');
      this.prevBtn = this.querySelector('[data-lightbox-prev]');
      this.nextBtn = this.querySelector('[data-lightbox-next]');
      this.backdropEl = this.querySelector('[data-lightbox-backdrop]');
      this.items = [];
      this.currentIndex = -1;
      this.lastFocused = null;

      if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.close());
      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.show(this.currentIndex - 1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.show(this.currentIndex + 1));

      // Click on backdrop closes
      if (this.backdropEl) {
        this.backdropEl.addEventListener('click', () => this.close());
      }

      // Image loaded → remove loading state
      if (this.imageEl) {
        this.imageEl.addEventListener('load', () => this.classList.remove('is-loading'));
        this.imageEl.addEventListener('error', () => this.classList.remove('is-loading'));
      }

      // Keyboard nav
      document.addEventListener('keydown', (e) => {
        if (!this.classList.contains('is-open')) return;
        if (e.key === 'Escape') {
          e.preventDefault();
          this.close();
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          this.show(this.currentIndex - 1);
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          this.show(this.currentIndex + 1);
        }
      });

      // Touch swipe nav
      let touchStartX = 0;
      let touchStartY = 0;
      this.addEventListener('touchstart', (e) => {
        if (!this.classList.contains('is-open') || !e.touches[0]) return;
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
      }, { passive: true });
      this.addEventListener('touchend', (e) => {
        if (!this.classList.contains('is-open') || !e.changedTouches[0]) return;
        const dx = e.changedTouches[0].clientX - touchStartX;
        const dy = e.changedTouches[0].clientY - touchStartY;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
          this.show(this.currentIndex + (dx < 0 ? 1 : -1));
        }
      }, { passive: true });

      // Global click delegation for triggers
      document.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-clown-lightbox]');
        if (!trigger) return;
        e.preventDefault();
        this.openFromTrigger(trigger);
      });
    }

    openFromTrigger(trigger) {
      const group = trigger.dataset.lightboxGroup || 'default';
      this.items = Array.from(
        document.querySelectorAll(`[data-clown-lightbox][data-lightbox-group="${group}"]`)
      );
      const idx = this.items.indexOf(trigger);
      this.lastFocused = document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      this.show(idx === -1 ? 0 : idx);
      if (this.closeBtn) this.closeBtn.focus();
    }

    show(index) {
      if (this.items.length === 0) return;
      const len = this.items.length;
      if (index < 0) index = len - 1;
      if (index >= len) index = 0;
      this.currentIndex = index;
      const item = this.items[index];

      const innerImg = item.querySelector('img');
      const src = item.dataset.fullSrc || (innerImg ? innerImg.src : '');
      const srcset = item.dataset.fullSrcset || '';
      const sizes = item.dataset.fullSizes || '';
      const alt = item.dataset.fullAlt || (innerImg ? innerImg.alt : '') || '';

      // Show loading state if image actually changes
      if (this.imageEl && this.imageEl.src !== src) {
        this.classList.add('is-loading');
      }

      this.imageEl.removeAttribute('srcset');
      this.imageEl.removeAttribute('sizes');
      this.imageEl.src = src;
      if (srcset) this.imageEl.srcset = srcset;
      if (sizes) this.imageEl.sizes = sizes;
      this.imageEl.alt = alt;

      // Already cached → no loading flash
      if (this.imageEl.complete && this.imageEl.naturalWidth > 0) {
        this.classList.remove('is-loading');
      }

      if (this.captionEl) this.captionEl.textContent = alt;
      if (this.counterEl) {
        this.counterEl.textContent = len > 1 ? `${index + 1} / ${len}` : '';
      }

      const hasMultiple = len > 1;
      if (this.prevBtn) this.prevBtn.hidden = !hasMultiple;
      if (this.nextBtn) this.nextBtn.hidden = !hasMultiple;
    }

    close() {
      this.classList.remove('is-open');
      this.classList.remove('is-loading');
      this.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      if (this.lastFocused && typeof this.lastFocused.focus === 'function') {
        this.lastFocused.focus();
      }
    }
  }

  if (!customElements.get('clown-lightbox')) {
    customElements.define('clown-lightbox', ClownLightbox);
  }
})();
